/**
 * Complete list of German medical specializations (Facharztbezeichnungen)
 * Based on the German Medical Association (Bundesärztekammer) specialization catalog
 *
 * Note: This list is mandatory for substitutes, optional for providers
 */

export const MEDICAL_SPECIALIZATIONS = [
  // Allgemeinmedizin
  'Facharzt für Allgemeinmedizin',
  'Facharzt für Allgemein- und Familienmedizin',

  // Anästhesiologie
  'Facharzt für Anästhesiologie',

  // Anatomie
  'Facharzt für Anatomie',

  // Arbeitsmedizin
  'Facharzt für Arbeitsmedizin',

  // Augenheilkunde
  'Facharzt für Augenheilkunde',

  // Biochemie
  'Facharzt für Biochemie',

  // Chirurgie
  'Facharzt für Allgemeine Chirurgie',
  'Facharzt für Gefäßchirurgie',
  'Facharzt für Herzchirurgie',
  'Facharzt für Kinderchirurgie',
  'Facharzt für Orthopädie und Unfallchirurgie',
  'Facharzt für Plastische und Ästhetische Chirurgie',
  'Facharzt für Thoraxchirurgie',
  'Facharzt für Visceralchirurgie',

  // Frauenheilkunde und Geburtshilfe
  'Facharzt für Frauenheilkunde und Geburtshilfe',

  // Hals-Nasen-Ohrenheilkunde
  'Facharzt für Hals-Nasen-Ohrenheilkunde',

  // Haut- und Geschlechtskrankheiten
  'Facharzt für Haut- und Geschlechtskrankheiten',

  // Humangenetik
  'Facharzt für Humangenetik',

  // Hygiene und Umweltmedizin
  'Facharzt für Hygiene und Umweltmedizin',

  // Innere Medizin
  'Facharzt für Innere Medizin',
  'Facharzt für Innere Medizin und Angiologie',
  'Facharzt für Innere Medizin und Endokrinologie und Diabetologie',
  'Facharzt für Innere Medizin und Gastroenterologie',
  'Facharzt für Innere Medizin und Hämatologie und Onkologie',
  'Facharzt für Innere Medizin und Kardiologie',
  'Facharzt für Innere Medizin und Nephrologie',
  'Facharzt für Innere Medizin und Pneumologie',
  'Facharzt für Innere Medizin und Rheumatologie',

  // Kinder- und Jugendmedizin
  'Facharzt für Kinder- und Jugendmedizin',
  'Facharzt für Kinder- und Jugendmedizin mit Schwerpunkt Kinderkardiologie',
  'Facharzt für Kinder- und Jugendmedizin mit Schwerpunkt Neonatologie',
  'Facharzt für Kinder- und Jugendmedizin mit Schwerpunkt Neuropädiatrie',

  // Kinder- und Jugendpsychiatrie und -psychotherapie
  'Facharzt für Kinder- und Jugendpsychiatrie und -psychotherapie',

  // Laboratoriumsmedizin
  'Facharzt für Laboratoriumsmedizin',

  // Mikrobiologie, Virologie und Infektionsepidemiologie
  'Facharzt für Mikrobiologie, Virologie und Infektionsepidemiologie',

  // Mund-Kiefer-Gesichtschirurgie
  'Facharzt für Mund-Kiefer-Gesichtschirurgie',

  // Neurochirurgie
  'Facharzt für Neurochirurgie',

  // Neurologie
  'Facharzt für Neurologie',

  // Nuklearmedizin
  'Facharzt für Nuklearmedizin',

  // Öffentliches Gesundheitswesen
  'Facharzt für Öffentliches Gesundheitswesen',

  // Pathologie
  'Facharzt für Pathologie',
  'Facharzt für Neuropathologie',

  // Pharmakologie
  'Facharzt für Pharmakologie',
  'Facharzt für Pharmakologie und Toxikologie',

  // Physikalische und Rehabilitative Medizin
  'Facharzt für Physikalische und Rehabilitative Medizin',

  // Physiologie
  'Facharzt für Physiologie',

  // Psychiatrie und Psychotherapie
  'Facharzt für Psychiatrie und Psychotherapie',

  // Psychosomatische Medizin und Psychotherapie
  'Facharzt für Psychosomatische Medizin und Psychotherapie',

  // Radiologie
  'Facharzt für Radiologie',
  'Facharzt für Kinderradiologie',
  'Facharzt für Neuroradiologie',

  // Rechtsmedizin
  'Facharzt für Rechtsmedizin',

  // Strahlentherapie
  'Facharzt für Strahlentherapie',

  // Transfusionsmedizin
  'Facharzt für Transfusionsmedizin',

  // Urologie
  'Facharzt für Urologie',

  // Sonstige / In Weiterbildung
  'In Weiterbildung (kein Facharzt)',
  'Praktischer Arzt / Praktische Ärztin',
  'Arzt / Ärztin ohne Gebietsbezeichnung',
] as const;

export type MedicalSpecialization = (typeof MEDICAL_SPECIALIZATIONS)[number];

/**
 * General / non-specialist options that should appear first in selection
 * dropdowns, since they are the most commonly chosen entries.
 */
export const GENERAL_SPECIALIZATIONS: readonly MedicalSpecialization[] = [
  'In Weiterbildung (kein Facharzt)',
  'Praktischer Arzt / Praktische Ärztin',
  'Arzt / Ärztin ohne Gebietsbezeichnung',
];

/**
 * Specialization order used for selection dropdowns: the general / non-specialist
 * options first, followed by the full Facharzt catalog in its canonical order.
 */
export const SPECIALIZATIONS_SELECT_ORDER: readonly MedicalSpecialization[] = [
  ...GENERAL_SPECIALIZATIONS,
  ...MEDICAL_SPECIALIZATIONS.filter((spec) => !GENERAL_SPECIALIZATIONS.includes(spec)),
];

/**
 * Helper function to check if a string is a valid medical specialization
 */
export const isValidSpecialization = (value: string): value is MedicalSpecialization => {
  return MEDICAL_SPECIALIZATIONS.includes(value as MedicalSpecialization);
};

/**
 * Get specializations grouped by category for better UI organization
 */
export const getGroupedSpecializations = () => {
  return {
    Allgemeinmedizin: MEDICAL_SPECIALIZATIONS.slice(0, 2),
    Anästhesiologie: MEDICAL_SPECIALIZATIONS.slice(2, 3),
    Anatomie: MEDICAL_SPECIALIZATIONS.slice(3, 4),
    Arbeitsmedizin: MEDICAL_SPECIALIZATIONS.slice(4, 5),
    Augenheilkunde: MEDICAL_SPECIALIZATIONS.slice(5, 6),
    Biochemie: MEDICAL_SPECIALIZATIONS.slice(6, 7),
    Chirurgie: MEDICAL_SPECIALIZATIONS.slice(7, 15),
    'Frauenheilkunde und Geburtshilfe': MEDICAL_SPECIALIZATIONS.slice(15, 16),
    'Hals-Nasen-Ohrenheilkunde': MEDICAL_SPECIALIZATIONS.slice(16, 17),
    'Haut- und Geschlechtskrankheiten': MEDICAL_SPECIALIZATIONS.slice(17, 18),
    Humangenetik: MEDICAL_SPECIALIZATIONS.slice(18, 19),
    'Hygiene und Umweltmedizin': MEDICAL_SPECIALIZATIONS.slice(19, 20),
    'Innere Medizin': MEDICAL_SPECIALIZATIONS.slice(20, 29),
    'Kinder- und Jugendmedizin': MEDICAL_SPECIALIZATIONS.slice(29, 33),
    'Kinder- und Jugendpsychiatrie': MEDICAL_SPECIALIZATIONS.slice(33, 34),
    Laboratoriumsmedizin: MEDICAL_SPECIALIZATIONS.slice(34, 35),
    Mikrobiologie: MEDICAL_SPECIALIZATIONS.slice(35, 36),
    'Mund-Kiefer-Gesichtschirurgie': MEDICAL_SPECIALIZATIONS.slice(36, 37),
    Neurochirurgie: MEDICAL_SPECIALIZATIONS.slice(37, 38),
    Neurologie: MEDICAL_SPECIALIZATIONS.slice(38, 39),
    Nuklearmedizin: MEDICAL_SPECIALIZATIONS.slice(39, 40),
    'Öffentliches Gesundheitswesen': MEDICAL_SPECIALIZATIONS.slice(40, 41),
    Pathologie: MEDICAL_SPECIALIZATIONS.slice(41, 43),
    Pharmakologie: MEDICAL_SPECIALIZATIONS.slice(43, 45),
    'Physikalische und Rehabilitative Medizin': MEDICAL_SPECIALIZATIONS.slice(45, 46),
    Physiologie: MEDICAL_SPECIALIZATIONS.slice(46, 47),
    'Psychiatrie und Psychotherapie': MEDICAL_SPECIALIZATIONS.slice(47, 48),
    'Psychosomatische Medizin': MEDICAL_SPECIALIZATIONS.slice(48, 49),
    Radiologie: MEDICAL_SPECIALIZATIONS.slice(49, 52),
    Rechtsmedizin: MEDICAL_SPECIALIZATIONS.slice(52, 53),
    Strahlentherapie: MEDICAL_SPECIALIZATIONS.slice(53, 54),
    Transfusionsmedizin: MEDICAL_SPECIALIZATIONS.slice(54, 55),
    Urologie: MEDICAL_SPECIALIZATIONS.slice(55, 56),
    Sonstige: MEDICAL_SPECIALIZATIONS.slice(56),
  };
};
